import * as React from "react";
import { boundMethod } from "autobind-decorator";
import { get, find, set, uniq, without } from "lodash";
// # interfaces
import { iUserAttribute, iUser } from "@/interfaces/users";
// # components
import { Select } from "@/components/Select";

interface iProps<iAttributeKeys = any> {
  /**
   * The attributes that determine which fields to display.
   */
  attributes: Array<iAttributeKeys & { key: iAttributeKeys }>;
  /**
   * Function called when a field is updated.
   */
  onChange: (values: Record<keyof iAttributeKeys, any>) => any;
  /**
   * The attribute values.
   * Keys should match
   */
  values: Record<keyof iAttributeKeys, any>;
}

type iChangeHandler = (input: React.ChangeEvent | string | string[]) => void;

export class UserAttributes extends React.Component<iProps> {
  /**
   * Renders the component.
   *
   * @returns {React.ReactElement}
   */
  public render(): React.ReactElement {
    if (!this.props.attributes) {
      return null;
    }

    return (
      <div className="user-attributes">
        {this.props.attributes.map(this.renderAttribute)}
      </div>
    );
  }

  /**
   * Render a single attribute field with a label.
   *
   * @param {iUserAttribute} attribute
   * @returns {React.ReactElement}
   */
  @boundMethod
  protected renderAttribute(attribute: iUserAttribute): React.ReactElement {
    let $input: React.ReactElement = null;

    switch (attribute.type) {
      case "checkboxes":
        $input = this.renderCheckboxFields(attribute);
        break;
      case "multi-select":
        $input = this.renderMultiSelectField(attribute);
        break;
      case "select":
        $input = this.renderSelectField(attribute);
        break;
      case "text":
      default:
        $input = this.renderTextField(attribute);
    }

    return (
      <div className="form__field-group">
        <label htmlFor="">
          {attribute.label}
          {attribute.required && "*"}
        </label>
        {$input}
      </div>
    );
  }

  @boundMethod
  protected renderCheckboxFields(
    attribute: iUserAttribute,
  ): React.ReactElement {
    const values: boolean[] = this.props.values[attribute.key] || [];

    return (
      <>
        {(attribute.options as any).options.map(
          (option: { value: string; label?: string }, key: number) => (
            <div className="checkbox">
              <label>
                <input
                  checked={!!values[key]}
                  name={attribute.key}
                  type="checkbox"
                  onChange={this.makeChangeHandler(attribute, key)}
                />
                <span>{option.label || option.value}</span>
              </label>
            </div>
          ),
        )}
      </>
    );
  }

  @boundMethod
  protected renderMultiSelectField(
    attribute: iUserAttribute,
  ): React.ReactElement {
    const values: string[] = this.props.values[attribute.key] || [];
    const changeHandler = this.makeChangeHandler(attribute);

    return (
      <>
        <Select
          items={(attribute.options as any).fetch}
          onSelect={changeHandler}
        />
        <div className="list">
          {values.map((item) => (
            <div className="list__item list-item">
              {item}
              <div className="list-item__controls">
                <button
                  className="list-item__control"
                  onClick={changeHandler.bind(this, { value: item })}
                  title="Remove"
                >
                  <i className="fa fa-trash list-item__icon" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  @boundMethod
  protected renderSelectField(attribute: iUserAttribute): React.ReactElement {
    const value: null | string = this.props.values[attribute.key];
    const selectValue = value ? { value, label: value } : null;

    return (
      <Select
        items={(attribute.options as any).options.map((option) => ({
          label: option.value,
          value: option.value,
        }))}
        onSelect={this.makeChangeHandler(attribute)}
        value={selectValue}
      />
    );
  }

  @boundMethod
  protected renderTextField(attribute: iUserAttribute): React.ReactElement {
    return (
      <input
        className="input"
        onChange={this.makeChangeHandler(attribute)}
        required={attribute.required}
        value={this.props.values[attribute.key]}
      />
    );
  }

  /**
   * Builds a function that handles changes on a field.
   *
   * @param {iUserAttribute} attribute
   * @param {any=} option
   * @returns {iChangeHandler}
   */
  @boundMethod
  protected makeChangeHandler(
    attribute: iUserAttribute,
    option?: any,
  ): iChangeHandler {
    return (input: React.ChangeEvent | string | string[]): void => {
      // extract value from the input
      const value = (input as any).target ? (input as any).target.value : input;

      // make a copy of the values object so we can modify it
      const values = { ...this.props.values };

      switch (attribute.type) {
        case "checkboxes":
          if (get(values, [attribute.key], [])[option] === 1) {
            // disable the option
            set(values, `${[attribute.key]}[${option}]`, 0);
          } else {
            // enable the option
            set(values, `${[attribute.key]}[${option}]`, 1);
          }
          break;
        case "multi-select":
          if (get(values, [attribute.key], []).includes(value.value)) {
            // remove the value
            set(
              values,
              [attribute.key],
              without(get(values, [attribute.key], []), value.value),
            );
          } else {
            // insert the value
            set(
              values,
              [attribute.key],
              uniq([...get(values, [attribute.key], []), value.value]),
            );
          }
          break;
        case "select":
          set(values, [attribute.key], value.value);
          break;
        case "text":
        default:
          set(values, [attribute.key], value);
      }

      this.props.onChange(values);
    };
  }
}
